import { useConversation } from '@/contexts/ConversationContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Content, Message } from '@shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { messageInsertedConversationUpdate } from './cacheUpdates.ts';
import { FETCH_ON_MOUNT } from '@/lib/queryClient';

export const useMessagesQuery = () => {
  const { conversation } = useConversation();
  // Gate on a resolved user, mirroring EditorView's conversation query
  // (`enabled: !!conversationId && authReady`). On a cold load `user` is null
  // for a tick while auth resolves; without this gate the messages query was
  // "enabled" with only conversation.id and settled to its `initialData: []`,
  // so the chat rendered ZERO bubbles + the viewer went grey during the
  // auth-race window (the #295/#297 grey-screen). Waiting for user.id keeps the
  // two queries in lockstep so the editor never paints an empty settled state
  // before auth lands; the continuous overlay (expectsModel) covers the wait.
  const { user } = useAuth();
  return useQuery<Message[]>({
    enabled: !!conversation.id && !!user?.id,
    queryKey: ['messages', conversation.id],
    // initialData:[] keeps isLoading false on reopen (no grey flash);
    // FETCH_ON_MOUNT forces the initial fetch the global staleTime would
    // otherwise skip — the empty-chat-on-cold-reopen bug.
    initialData: [],
    ...FETCH_ON_MOUNT,
    queryFn: async () => {
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversation.id)
        .order('created_at', { ascending: true })
        .overrideTypes<
          Array<{ content: Content; role: 'user' | 'assistant' }>
        >();

      if (messagesError) throw messagesError;

      return messagesData || [];
    },
  });
};

export function useInsertMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      message: Omit<Message, 'id' | 'created_at' | 'rating'>,
    ) => {
      const { data, error } = await supabase
        .from('messages')
        .insert([{ ...message }])
        .select()
        .single()
        .overrideTypes<{ content: Content; role: 'user' | 'assistant' }>();

      if (error) throw error;

      return data;
    },
    onSuccess(newMessage) {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        newMessage.conversation_id,
      );
    },
    onError(error, message) {
      Sentry.captureException(error, {
        extra: {
          hook: 'useInsertMessageMutation',
          message,
        },
      });
    },
  });
}

export function useUpdateMessageOptimisticMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ message }: { message: Message }) => {
      const { data: updatedMessage, error: messageError } = await supabase
        .from('messages')
        .update({
          // only content and rating get updated
          content: message.content,
          rating: message.rating,
        })
        .eq('id', message.id)
        .eq('conversation_id', message.conversation_id)
        .select()
        .single();

      if (messageError) throw messageError;

      return updatedMessage as Message;
    },
    onMutate: async ({ message }) => {
      await queryClient.cancelQueries({
        queryKey: ['messages', message.conversation_id],
      });
      const oldMessages = queryClient.getQueryData<Message[]>([
        'messages',
        message.conversation_id,
      ]);
      queryClient.setQueryData(
        ['messages', message.conversation_id],
        oldMessages?.map((msg) =>
          msg.id === message.id ? { ...msg, ...message } : msg,
        ),
      );
      return { oldMessages };
    },
    onSettled(_data, _error, { message }) {
      queryClient.invalidateQueries({
        queryKey: ['messages', message.conversation_id],
      });
    },
    onError(error, { message }, context) {
      Sentry.captureException(error, {
        extra: {
          hook: 'useUpdateMessageOptimisticMutation',
          message,
        },
      });
      queryClient.setQueryData(
        ['messages', message.conversation_id],
        context?.oldMessages,
      );
    },
  });
}

export function useRestoreMessageMutation() {
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationFn: async (messageToRestore: Message) => {
      await insertMessageAsync({
        role: messageToRestore.role,
        content: messageToRestore.content,
        parent_message_id: messageToRestore.parent_message_id ?? null,
        conversation_id: messageToRestore.conversation_id,
      });
    },
    onError: (error, messageToRestore) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useRestoreMessageMutation',
          messageToRestore,
        },
      });
    },
  });
}

export function useChangeRatingMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateMessageOptimistic } =
    useUpdateMessageOptimisticMutation();

  const messages = queryClient.getQueryData<Message[]>([
    'messages',
    conversationId,
  ]);

  return useMutation({
    mutationKey: ['change-rating', conversationId],
    mutationFn: async ({
      messageId,
      rating,
    }: {
      messageId: string;
      rating: number;
    }) => {
      const oldMessage = messages?.find((msg) => msg.id === messageId);
      if (!oldMessage) return;
      updateMessageOptimistic({ message: { ...oldMessage, rating } });
    },
  });
}
